import React, {
    useContext,
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useId,
    useCallback,
    cloneElement,
    Children,
    isValidElement,
    forwardRef,
    useMemo,
} from "react";

import { StyledTriggerWrapper } from "./styled";
import { LightboxContext } from "./context";
import { useFloatingSetup } from "./hooks/useFloatingSetup";
import { CascadeContent } from "./renderers/CascadeContent";
import { InlineRootContent } from "./renderers/InlineRootContent";
import { InlineChildContent } from "./renderers/InlineChildContent";

import type {
    LightboxContextValue,
    LightboxProps,
    LightboxTriggerProps,
    LightboxContentProps,
} from "./types";

export { LightboxContext };

/**
 * Hook to access the Lightbox context.
 * Must be used within a Lightbox component.
 * @throws Error if used outside of Lightbox
 */
const useLightboxContext = () => {
    const context = useContext(LightboxContext);
    if (!context) {
        throw new Error("Lightbox components must be used within a Lightbox");
    }
    return context;
};

/**
 * Public hook to access the Lightbox context.
 * Returns null if used outside of a Lightbox — safe for optional consumption.
 */
export const useLightbox = () => useContext(LightboxContext);

/**
 * Root component that provides floating UI context for the lightbox.
 * Supports both controlled and uncontrolled open state, click or hover triggers.
 *
 * @param multilayer - When false (default), nested lightboxes cascade as separate floating panels.
 *                     When true, nested lightboxes render side-by-side in a shared inline container.
 *                     Propagates to all descendants.
 *
 * @example
 * ```tsx
 * // Uncontrolled (click to open)
 * <Lightbox>
 *   <LightboxTrigger><button>Open</button></LightboxTrigger>
 *   <LightboxContent>Content here</LightboxContent>
 * </Lightbox>
 *
 * // Controlled
 * <Lightbox open={isOpen} onOpenChange={setIsOpen}>
 *   ...
 * </Lightbox>
 *
 * // Hover mode
 * <Lightbox onHover>
 *   ...
 * </Lightbox>
 *
 * // Disabled
 * <Lightbox enabled={false}>
 *   ...
 * </Lightbox>
 *
 * // Inline mode (side-by-side panels)
 * <Lightbox multilayer>
 *   <LightboxTrigger><button>Open</button></LightboxTrigger>
 *   <LightboxContent>
 *     <Lightbox placement="right-start">
 *       <LightboxTrigger><div>Sub item</div></LightboxTrigger>
 *       <LightboxContent>Nested panel appears inline</LightboxContent>
 *     </Lightbox>
 *   </LightboxContent>
 * </Lightbox>
 * ```
 */
export function Lightbox({
    children,
    open: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    onHover = false,
    placement = "bottom-start",
    mainAxisOffset = 2,
    crossAxisOffset = 2,
    enabled = true,
    anchorEl,
    shiftBoundary,
    multilayer,
}: LightboxProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const listRef = useRef<(HTMLElement | null)[]>([]);
    const contentId = useId();

    // Reset internal open state when disabled
    useEffect(() => {
        if (!enabled) {
            setUncontrolledOpen(false);
        }
    }, [enabled]);

    // Determine if component is controlled or uncontrolled
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;

    const setOpen = useCallback(
        (newOpen: boolean) => {
            if (!enabled && newOpen) return;
            if (!isControlled) {
                setUncontrolledOpen(newOpen);
            }
            onOpenChange?.(newOpen);
            if (!newOpen) {
                setActiveIndex(null);
                listRef.current = [];
            }
        },
        [isControlled, onOpenChange, enabled]
    );

    // Inherit multilayer from parent if any ancestor set it
    const parentCtx = useContext(LightboxContext);
    const effectiveMultilayer = multilayer ?? parentCtx?.multilayer ?? false;
    const depth = parentCtx ? parentCtx.depth + 1 : 0;

    // Nested multilayer Lightboxes (depth > 0) render as inline child panels
    // via InlineChildContent, which doesn't use floating-ui at all.
    // Skip floating-ui to avoid registering document-level dismiss listeners
    // that would fire on every item Lightbox when the overlay is clicked,
    // causing a catastrophic full-tree re-render (~6.5s for 46K fibers).
    const skipFloating = effectiveMultilayer && depth > 0;
    const noop = useCallback(() => {}, []);

    // Floating-UI setup — hooks must always be called (React rules),
    // but with open:false + enabled:false no listeners or positioning activate.
    const {
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        isMounted,
        transitionStyles,
        getItemProps,
    } = useFloatingSetup({
        open: skipFloating ? false : open,
        setOpen: skipFloating ? noop : setOpen,
        placement,
        mainAxisOffset,
        crossAxisOffset,
        onHover: skipFloating ? false : onHover,
        enabled: skipFloating ? false : enabled,
        activeIndex: skipFloating ? null : activeIndex,
        setActiveIndex: skipFloating ? noop : setActiveIndex,
        listRef,
        shiftBoundary,
    });

    // When anchorEl is provided, override the floating-ui reference element
    // so LightboxContent positions relative to the external anchor.
    // useLayoutEffect (not useEffect) so the reference is set before the
    // browser paints — otherwise floating-ui emits its default {top:0,left:0}
    // styles for the first frame and the panel flashes at viewport (0,0)
    // before snapping to the anchor on the next commit.
    useLayoutEffect(() => {
        if (skipFloating || anchorEl === undefined) return;
        refs.setReference(anchorEl);
        return () => { refs.setReference(null); };
    }, [anchorEl, refs, skipFloating]);

    const contextValue = useMemo<LightboxContextValue>(() => {
        if (skipFloating) {
            return {
                open,
                setOpen,
                onHover,
                enabled,
                multilayer: effectiveMultilayer,
                depth,
                contentId,
                refs: { setReference: () => {}, setFloating: () => {} },
                floatingStyles: {},
                getFloatingProps: (p?: any) => p ?? {},
                context: null,
                isMounted: open,
                transitionStyles: {},
                activeIndex: null,
                setActiveIndex: () => {},
                listRef: { current: [] },
                getItemProps: (p?: any) => p ?? {},
                getReferenceProps: (userProps?: any) => ({
                    ...userProps,
                    onClick: (e: React.MouseEvent) => {
                        userProps?.onClick?.(e);
                        setOpen(!open);
                    },
                }),
            };
        }
        return {
            open,
            setOpen,
            onHover,
            enabled,
            multilayer: effectiveMultilayer,
            depth,
            contentId,
            refs,
            floatingStyles,
            getReferenceProps,
            getFloatingProps,
            context,
            isMounted,
            transitionStyles,
            activeIndex,
            setActiveIndex,
            listRef,
            getItemProps: getItemProps as LightboxContextValue["getItemProps"],
        };
    }, [
        skipFloating,
        open,
        setOpen,
        onHover,
        enabled,
        effectiveMultilayer,
        depth,
        contentId,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        context,
        isMounted,
        transitionStyles,
        activeIndex,
        setActiveIndex,
        getItemProps,
    ]);

    return (
        <LightboxContext.Provider value={contextValue}>
            {children}
        </LightboxContext.Provider>
    );
}

/**
 * Trigger element that opens/closes the lightbox.
 *
 * @param asChild - When true, merges props onto the child element. When false, wraps children in a span.
 *
 * @example
 * ```tsx
 * // With asChild (merges props onto button)
 * <LightboxTrigger asChild>
 *   <button>Open Menu</button>
 * </LightboxTrigger>
 *
 * // Without asChild (wraps in span)
 * <LightboxTrigger>
 *   <MyComponent />
 * </LightboxTrigger>
 * ```
 */
export function LightboxTrigger({ children, asChild = false }: LightboxTriggerProps) {
    const { refs, getReferenceProps } = useLightboxContext();

    if (asChild && isValidElement(children)) {
        const child = Children.only(children);
        const referenceProps = getReferenceProps(child.props as React.HTMLProps<Element>);

        return cloneElement(child, {
            ref: refs.setReference,
            ...referenceProps,
        } as any);
    }

    return (
        <StyledTriggerWrapper
            ref={refs.setReference as React.Ref<HTMLSpanElement>}
            {...getReferenceProps()}
        >
            {children}
        </StyledTriggerWrapper>
    );
}

/**
 * Floating content container rendered in a portal.
 * Handles positioning, focus management, and keyboard interactions.
 *
 * Delegates to one of three renderers based on mode:
 * - Cascade: independent floating panel (multilayer=false)
 * - Inline root: shared flex container (multilayer=true, depth=0)
 * - Inline child: portaled flex sibling (multilayer=true, depth>0)
 *
 * @example
 * ```tsx
 * <LightboxContent title="Menu" style={{ width: "240px" }}>
 *   <List options={options} onChange={handleChange} />
 * </LightboxContent>
 * ```
 */
export const LightboxContent = forwardRef<HTMLDivElement, LightboxContentProps>(
    function LightboxContent(props, ref) {
        const ctx = useLightboxContext();

        if (ctx.multilayer) {
            return ctx.depth === 0
                ? <InlineRootContent {...props} ref={ref} />
                : <InlineChildContent {...props} ref={ref} />;
        }

        return <CascadeContent {...props} ref={ref} />;
    }
);

export type {
    LightboxProps,
    LightboxTriggerProps,
    LightboxContentProps,
} from "./types";
